import { FilterOptionsState } from '@mui/material';
import { createFilterOptions } from '@mui/material/Autocomplete';
import { keyBy } from 'lodash';
import { useCallback, useMemo, useState } from 'react';
import { Control } from 'react-hook-form';

import { TypeaheadSelectInputOption } from '@/components/form/baseInputs/inputTypes';
import { FormAwareTypeaheadMultiSelectInput } from '@/components/form/formAwareInputs/FormAwareTypeaheadMultiSelectInput';
import { useFeedback } from '@/components/notifications/Feedback/useFeedback';
import { useFormContext } from '@/components/react-hook-form';
import { NON_INPUT_DOCUMENT_TYPES } from '@/modules/documents/documentTypePolicies';
import { getCompatibleDocumentTypes } from '@/modules/documents/ManageDocumentAmendments/utils/ManageDocumentAmendments.utils';
import { getNodes } from '@/utils/graphqlUtils';

import { useCreateMemoModalDocumentsQuery } from '../graphql/CreateMemoModalDocuments.generated';
import { CreateMemoModalFormShape } from './CreateMemoModal.types';

const EMPTY_DOCUMENT_IDS: string[] = [];

const filterDocumentOptions = createFilterOptions<
  TypeaheadSelectInputOption<string>
>({
  stringify: (option) => `${option.display} ${option.secondaryText ?? ''}`,
});

export function getFilteredDocumentOptions(
  options: TypeaheadSelectInputOption<string>[],
  state: FilterOptionsState<TypeaheadSelectInputOption<string>>,
  selectedDocumentIdSet: Set<string>
) {
  const matchingOptions = filterDocumentOptions(options, state);
  const matchingOptionValues = new Set(
    matchingOptions.map((option) => option.value)
  );

  const selectedNonMatchingOptions = options.filter((option) => {
    return (
      selectedDocumentIdSet.has(option.value) &&
      !matchingOptionValues.has(option.value)
    );
  });

  return [...selectedNonMatchingOptions, ...matchingOptions];
}

interface DocumentPickerProps {
  control: Control<CreateMemoModalFormShape>;
  householdId: string;
  maxDocuments: number;
}

export function DocumentPicker({
  control,
  householdId,
  maxDocuments,
}: DocumentPickerProps) {
  const { watch } = useFormContext<CreateMemoModalFormShape>();
  const { createErrorFeedback } = useFeedback();
  const [inputValue, setInputValue] = useState('');
  const selectedDocumentIds = watch('documentIds') ?? EMPTY_DOCUMENT_IDS;

  const { data, loading } = useCreateMemoModalDocumentsQuery({
    fetchPolicy: 'cache-and-network',
    variables: {
      where: {
        hasHouseholdWith: [{ id: householdId }],
        typeNotIn: NON_INPUT_DOCUMENT_TYPES,
      },
    },
    onError: createErrorFeedback(
      `We couldn't load household documents. Please refresh and try again.`
    ),
  });

  const documents = useMemo(() => {
    return getNodes(data?.documents);
  }, [data?.documents]);
  const documentsById = useMemo(() => {
    return keyBy(documents, 'id');
  }, [documents]);

  const selectedDocumentType = useMemo(() => {
    const firstSelectedDocumentId = selectedDocumentIds?.[0];
    if (!firstSelectedDocumentId) {
      return undefined;
    }

    return documentsById[firstSelectedDocumentId]?.type;
  }, [documentsById, selectedDocumentIds]);

  const compatibleDocumentTypes = useMemo(() => {
    if (!selectedDocumentType) {
      return undefined;
    }
    // Types without a relationship group can only be compared with the same type.
    return new Set([
      selectedDocumentType,
      ...getCompatibleDocumentTypes(selectedDocumentType),
    ]);
  }, [selectedDocumentType]);

  const selectedDocumentIdSet = useMemo(() => {
    return new Set(selectedDocumentIds);
  }, [selectedDocumentIds]);

  const hasReachedSelectionLimit = selectedDocumentIds.length >= maxDocuments;

  const documentOptions = useMemo(() => {
    return documents.map((document) => {
      const isSelected = selectedDocumentIdSet.has(document.id);
      const shouldDisableByType =
        !!compatibleDocumentTypes &&
        !isSelected &&
        !compatibleDocumentTypes.has(document.type);
      const shouldDisableByLimit = hasReachedSelectionLimit && !isSelected;

      return {
        display: document.name,
        value: document.id,
        secondaryText: document.displayType,
        disabled: shouldDisableByType || shouldDisableByLimit,
      };
    });
  }, [
    compatibleDocumentTypes,
    documents,
    hasReachedSelectionLimit,
    selectedDocumentIdSet,
  ]);

  const filterOptions = useCallback(
    (
      options: TypeaheadSelectInputOption<string>[],
      state: FilterOptionsState<TypeaheadSelectInputOption<string>>
    ) => getFilteredDocumentOptions(options, state, selectedDocumentIdSet),
    [selectedDocumentIdSet]
  );

  return (
    <FormAwareTypeaheadMultiSelectInput<CreateMemoModalFormShape>
      id="create-memo-documents"
      control={control}
      fieldName="documentIds"
      label="Select documents to compare"
      options={documentOptions}
      inputValue={inputValue}
      onInputChange={setInputValue}
      filterOptions={filterOptions}
      placeholder="Search documents..."
      loading={loading}
      required
      helpText="Documents selected must be the same type, or an original document and its amendment."
      validation={{
        required: (value: unknown) => {
          const documentIds = value as string[] | undefined;
          if (!documentIds || documentIds.length !== maxDocuments) {
            return `Select ${maxDocuments} documents.`;
          }

          return undefined;
        },
      }}
    />
  );
}
